import StatusSwitch from 'components/StatusSwitch';
import { useTypeLabel } from 'views/Todo/hooks/useLabel';
import PolicyTable from './PolicyTable';
import { updatePolicy } from 'views/Todo/service';
import {
  Button,
  message,
  Space,
  Modal,
  Checkbox,
  Spin,
  CheckboxProps,
  InputNumber,
  Tooltip,
  Input,
  Tag,
} from 'antd';
import {
  reduce,
  uniqBy,
  find,
  findIndex,
  set,
  cloneDeep,
  get,
  filter,
  map,
  forEach,
} from 'lodash-es';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { T, useTranslate } from '@tolgee/react';
import { useTodoType } from './TodoType';
import { deletePolicy } from './api';
import { v4 as uuidV4 } from 'uuid';
import { produce } from 'immer';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { regionMonetaryUnitMap } from 'services/config/region';
import { PlusOutlined, CloseCircleFilled } from '@ant-design/icons';
import { css } from '@emotion/css';

export interface PolicyPreviewProps {
  policy: Todo.Policy;
  onUpdate: () => Promise<any>;
  onEdit: (
    policy: Todo.Policy,
    resultType: Todo.ResultType,
    resultTypes: Todo.ResultType[],
  ) => void;
  existNames?: string[];
  regions?: General.UpperCaseRegion[];
  filterStoreListRegions?: General.UpperCaseRegion[];
}

function PolicyPreview(props: PolicyPreviewProps) {
  const { policy, onUpdate, onEdit, existNames = [], regions = [], filterStoreListRegions } = props;

  const { type, enable, name, enableManualCreate } = policy;

  const { t, getTypeLabel } = useTypeLabel();
  const getRegionLabel = useGetRegionLabel();

  const dataSource = useMemo(() => {
    const policyTemplates = cloneDeep(policy.templates);
    const resultType = map(uniqBy(policyTemplates, 'resultType'), (e) => e.resultType);
    const newTemplates: Todo.PolicyTemplate[] = [];
    const filterRegionTemplates = filter(policyTemplates, (e) =>
      filterStoreListRegions ? filterStoreListRegions?.includes(e.region) : true,
    ); // 过滤下当前店铺的
    forEach(resultType, (resultType) => {
      const enable = find(filterRegionTemplates, (e) => e.resultType === resultType && e.enable);
      const notEnable = find(policyTemplates, (e) => e.resultType === resultType);
      if (!!enable) {
        newTemplates.push(enable);
      } else {
        newTemplates.push(notEnable!);
      }
    });

    const r = uniqBy(
      filter(newTemplates, () => true),
      'resultType',
    );
    return reduce(
      r,
      (acc, cur) => {
        if (cur.parentResultType) {
          const target = find(acc, (e) => e.resultType === cur.parentResultType);
          if (target) {
            target.children ? target.children.push(cur) : (target.children = [cur]);
          } else {
            acc.push({
              resultType: cur.parentResultType,
              executeContext: [],
              children: [cur],
            } as unknown as Todo.PolicyTemplate);
          }
        } else {
          acc.push(cur);
        }
        return acc;
      },
      [] as Todo.PolicyTemplate[],
    );
  }, [policy, filterStoreListRegions]);

  const [instance, open] = useTodoType({
    taskId: policy.taskId,
    refresh: onUpdate,
    existNames: existNames.filter((e) => e !== policy.name),
  });
  const { qaEnable, whiteListConfig: { LLM } = {} } = useOrgConfigStateValue();
  const resultTypes = dataSource.map((e) => e.resultType);
  const policyName = policy.source === 0 ? getTypeLabel(type) : name;
  const [policyFieldKey, setPolicyFieldKey] = useState<
    Partial<Record<keyof typeof policy, string>>
  >(() => {
    return {
      overdueReceivedTimeout: uuidV4(),
      overdueDeliveryTimeout: uuidV4(),
      overdueCollectTimeout: uuidV4(),
      regionFreights: uuidV4(),
    };
  });
  const [regionCheckedMap, setRegionCheckedMap] = useState(() => {
    const allTrue = policy.regionFreights.length === 0;
    return reduce(
      regions,
      (acc, cur) => {
        acc[cur] = allTrue || !!find(policy.regionFreights, (e) => e.region === cur);
        return acc;
      },
      {} as Record<General.UpperCaseRegion, boolean>,
    );
  });
  const regionAmountMap = useMemo(() => {
    return reduce(
      policy.regionFreights,
      (acc, cur) => {
        acc[cur.region] = cur.freight;
        return acc;
      },
      {} as Record<General.UpperCaseRegion, number>,
    );
  }, [policy]);
  const updatePolicyField = (key: keyof typeof policy, value: any) => {
    if (policy[key] !== value) {
      Modal.confirm({
        title: t('确认要修改吗？'),
        content: t('修改生效条件前下单的订单将不会判断是否满足生效条件。'),
        onOk: () => {
          return updatePolicy({
            ...policy,
            [key]: value,
          })
            .then(onUpdate)
            .then(() => {
              message.success(t('operation.saveSucceed'));
            })
            .catch(() => {
              message.error(t('operation.saveFailed'));
            })
            .finally(() => {
              setPolicyFieldKey(
                produce((draft) => {
                  draft[key] = uuidV4();
                }),
              );
            });
        },
      });
    }
  };
  const { whiteListConfig: { DRJL = false } = {} } = useOrgConfigStateValue();
  if (policy.type === 12 && !DRJL) return null;

  return (
    <Container>
      {instance}
      <header>
        <div className="title flex items-center">
          <h3>{policyName}</h3>
          {type === 8 && (
            <span className="ml-[4px] text-[12px] text-[#515359]">
              {t('当前待办类型仅对未退货订单生效，订单退货后不会再判断是否额外收取运费。')}
            </span>
          )}
          {type === 9 && (
            <span className="ml-[4px] text-[12px] text-[#515359]">
              {t('当前待办类型仅对现货订单生效，不会影响预售订单')}
            </span>
          )}
          {type === 10 && (
            <span className="ml-[4px] text-[12px] text-[#515359]">
              {t('当前待办类型仅对现货订单生效，不会影响预售订单')}
            </span>
          )}
          {[1, 2, 0, 12, 13].includes(type) && (
            <span className="ml-[4px] text-[12px] text-[#515359]">
              {t('此类待办基于AI语义识别自动创建，请确保已开启AI接待功能')}
            </span>
          )}
        </div>

        <Space>
          <Button
            onClick={() => {
              open({
                source: policy.source,
                typeName: policyName,
                policy_id: policy.id,
                subTypes: policy.subTypes,
                subTypeTrees: policy.subTypeTrees,
                enableRefundGoods: policy.enableRefundGoods,
                enableRefundOnly: policy.enableRefundOnly,
                enable: policy.enable,
                enableManualCreate: policy.enableManualCreate,
                enableKolContactStore: policy.enableKolContactStore,
                enableStoreContactKol: policy.enableStoreContactKol,
                kolKeyword: policy.kolKeyword,
              });
            }}
            type="link"
            className="!px-[2px]"
          >
            {t('marketing.编辑')}
          </Button>
          {policy.source === 1 && (
            <Button
              type="link"
              className="!px-[2px]"
              onClick={() => {
                Modal.confirm({
                  title: t('official_delete_tip'),
                  content: t('selfhosted_delete_content'),
                  onOk: () => {
                    deletePolicy(policy.id)
                      .then(() => {
                        onUpdate();
                        message.success(t('marketing.已删除'));
                      })
                      .catch(() => {
                        message.error(t('phrase.删除失败，请稍后再试'));
                      });
                  },
                });
              }}
            >
              {t('marketing.删除')}
            </Button>
          )}
        </Space>
      </header>
      <div>
        <dl>
          {policy.source === 0 &&
            ([7, 8, 9, 10, 11, 12, 13].includes(policy.type) ? true : qaEnable || LLM) && (
              <>
                <dt>{t('marketing.启用状态')}:</dt>
                <dd>
                  <StatusSwitch
                    checkedChildren={t('aide.开')}
                    unCheckedChildren={t('aide.关')}
                    checked={enable}
                    onChange={(enable) =>
                      updatePolicy({
                        ...policy,
                        enable,
                      })
                        .then(onUpdate)
                        .then(() => {
                          message.success(t('operation.saveSucceed'));
                        })
                        .catch(() => {
                          message.error(t('operation.saveFailed'));
                        })
                    }
                  />
                </dd>
              </>
            )}
          {policy.enable && (
            <>
              <dt className="ml-[24px]">{t('支持手动创建')}:</dt>
              <dd>
                <StatusSwitch
                  checkedChildren={t('aide.开')}
                  unCheckedChildren={t('aide.关')}
                  checked={enableManualCreate}
                  onChange={(enable) =>
                    updatePolicy({
                      ...policy,
                      enableManualCreate: enable,
                    })
                      .then(onUpdate)
                      .then(() => {
                        message.success(t('operation.saveSucceed'));
                      })
                      .catch(() => {
                        message.error(t('operation.saveFailed'));
                      })
                  }
                />
              </dd>
            </>
          )}
        </dl>
        {policy.enable && policy.source === 0 && type === 7 && (
          <>
            <dl>
              <dt>{t('自动创建的弃单范围')}:</dt>
              <dd>
                <StatusCheckbox
                  checked={policy.enableRefundOnly}
                  onChange={(e) => {
                    return updatePolicy({
                      ...policy,
                      enableRefundOnly: e.target.checked,
                    })
                      .then(onUpdate)
                      .then(() => {
                        message.success(t('operation.saveSucceed'));
                      })
                      .catch(() => {
                        message.error(t('operation.saveFailed'));
                      });
                  }}
                >
                  {t('退款已获批准（仅退款）')}
                </StatusCheckbox>
              </dd>
              <dd>
                <StatusCheckbox
                  checked={policy.enableRefundGoods}
                  onChange={(e) => {
                    return updatePolicy({
                      ...policy,
                      enableRefundGoods: e.target.checked,
                    })
                      .then(onUpdate)
                      .then(() => {
                        message.success(t('operation.saveSucceed'));
                      })
                      .catch(() => {
                        message.error(t('operation.saveFailed'));
                      });
                  }}
                >
                  {t('验证进行中（退货/退款）')}
                </StatusCheckbox>
              </dd>
            </dl>
          </>
        )}

        {policy.enable && policy.source === 0 && type === 8 && (
          <>
            <dl>
              <dt>{t('请选择生效站点')}:</dt>
              <dd className="flex">
                {regions.map((region) => {
                  return (
                    <StatusCheckbox
                      key={region}
                      checked={regionCheckedMap[region]}
                      onChange={(e) => {
                        const fn = () => {
                          setRegionCheckedMap(
                            produce((draft) => {
                              draft[region] = e.target.checked;
                            }),
                          );
                        };
                        if (e.target.checked === false) {
                          let index = findIndex(policy.regionFreights, (e) => e.region === region);
                          if (index > -1) {
                            let copyF = [...policy.regionFreights];
                            copyF.splice(index, 1);
                            return updatePolicy({
                              ...policy,
                              regionFreights: copyF,
                            })
                              .then(() => {
                                message.success(t('operation.saveSucceed'));
                              })
                              .catch(() => {
                                message.error(t('operation.saveFailed'));
                              })
                              .finally(() => {
                                fn();
                                setPolicyFieldKey(
                                  produce((draft) => {
                                    draft['regionFreights'] = uuidV4();
                                  }),
                                );
                              });
                          } else {
                            return Promise.resolve().then(() => fn());
                          }
                        } else {
                          return Promise.resolve().then(() => fn());
                        }
                      }}
                    >
                      {getRegionLabel(region)}
                    </StatusCheckbox>
                  );
                })}
              </dd>
            </dl>
            <dl>
              <dt className="flex-shrink-0 self-start">{t('设置生效条件')}:</dt>
              <dd>
                <div className="bg-[#fff] px-[12px] py-[8px] flex items-center flex-wrap gap-[8px]">
                  {regions.map((region) => {
                    if (!regionCheckedMap[region]) return null;
                    return (
                      <div className="flex items-center">
                        <div className="text-[12px] text-[#336fff] h-[24px] px-[8px] flex items-center mr-[16px] bg-[#f0f7ff]">
                          {getRegionLabel(region)}
                        </div>
                        <span className="text-[#84868c]">{t('卖家支付运费金额')}</span>
                        <Tooltip title={t('运费金额：订单收入中运费小计(实际)部分的支出金额')}>
                          <QuestionCircleOutlined
                            style={{ color: '#84868c', padding: '0px 4px' }}
                          />
                        </Tooltip>
                        <span className="text-[#84868c]">&gt;&nbsp;</span>
                        <div
                          className="w-[130px] [&_input::-webkit-input-placeholder]:!text-[12px]"
                          title={t('请输入金额')}
                        >
                          <InputNumber
                            controls={false}
                            key={`${policyFieldKey['regionFreights']}.${region}`}
                            addonAfter={
                              regionMonetaryUnitMap[region.toLowerCase() as General.Region]
                            }
                            precision={2}
                            placeholder={t('请输入金额')}
                            defaultValue={regionAmountMap[region]}
                            onPressEnter={(e) => {
                              e.target.blur();
                            }}
                            onBlur={(e) => {
                              const copyF: Todo.Policy['regionFreights'] = cloneDeep(
                                policy.regionFreights,
                              );
                              let i = findIndex(copyF, (e) => e.region === region);
                              if (i > -1) {
                                set(copyF, `${i}.freight`, Number(e.target.value));
                              } else {
                                copyF.push({
                                  region,
                                  freight: Number(e.target.value),
                                  currency:
                                    regionMonetaryUnitMap[region.toLowerCase() as General.Region],
                                });
                              }
                              updatePolicy({
                                ...policy,
                                regionFreights: copyF,
                              })
                                .then(onUpdate)
                                .then(() => {
                                  message.success(t('operation.saveSucceed'));
                                })
                                .catch(() => {
                                  message.error(t('operation.saveFailed'));
                                })
                                .finally(() => {
                                  setPolicyFieldKey(
                                    produce((draft) => {
                                      draft['regionFreights'] = uuidV4();
                                    }),
                                  );
                                });
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[12px] text-[#84868c] mt-[6px]">
                  {t(
                    '请输入物流商正常收取运费时，店铺为每一笔订单支付的运费金额，运费支出超过该金额的订单将视作额外收取运费',
                  )}
                </div>
              </dd>
            </dl>
          </>
        )}
        {policy.enable && policy.source === 0 && type === 9 && (
          <>
            <dl className="!items-start">
              <dt className="mt-1.5">{t('设置生效条件')}:</dt>
              <dd className="flex flex-wrap gap-4">
                <div className="flex">
                  <Checkbox
                    className="!mr-2 !mt-1.5"
                    checked={policy.overdueDeliveryByTrade}
                    onChange={({ target: { checked } }) =>
                      updatePolicyField('overdueDeliveryByTrade', checked)
                    }
                  />
                  <div>
                    <div className="flex items-center flex-wrap">
                      <T
                        keyName="买家下单后超过{{X}}小时，卖家未在卖家中心点击发货"
                        params={{
                          x: (
                            <div className="px-[4px]">
                              <InputNumber
                                key={policyFieldKey['overdueDeliveryTimeout']}
                                controls={false}
                                defaultValue={policy.overdueDeliveryTimeout || undefined}
                                min={1}
                                max={336}
                                precision={0}
                                onPressEnter={(e) => {
                                  e.target.blur();
                                }}
                                onBlur={(e) => {
                                  setTimeout(() => {
                                    updatePolicyField(
                                      'overdueDeliveryTimeout',
                                      Number(e.target.value),
                                    );
                                  }, 200);
                                }}
                              />
                            </div>
                          ),
                        }}
                      />
                    </div>
                    <span className="inline-block p-1 mt-1 bg-[#FCFAEE]">
                      {t('当前待办类型仅对现货订单生效，不会影响预售订单')}
                    </span>
                  </div>
                </div>
                <div className="flex mt-1.5">
                  <Checkbox
                    className="!mr-2"
                    checked={policy.overdueDeliveryByChat}
                    onChange={({ target: { checked } }) =>
                      updatePolicyField('overdueDeliveryByChat', checked)
                    }
                  />
                  <div>
                    <div>{t('买家来催促物流，已超出AI接待配置的发货时间')}</div>
                    <span className="inline-block p-1 mt-2 bg-[#FCFAEE]">
                      {t('此类待办基于AI语义识别自动创建，请确保已开启AI接待功能')}
                    </span>
                  </div>
                </div>
              </dd>
            </dl>
          </>
        )}
        {policy.enable && policy.source === 0 && type === 10 && (
          <>
            <dl>
              <dt>{t('设置生效条件')}:</dt>
              <dd>
                <div className="h-[48px] px-[12px] py-[8px] flex items-center flex-wrap">
                  <T
                    keyName="买家下单后超过{{X}}小时，订单尚未被揽收"
                    params={{
                      x: (
                        <div className="px-[4px]">
                          <InputNumber
                            key={policyFieldKey['overdueCollectTimeout']}
                            controls={false}
                            defaultValue={policy.overdueCollectTimeout || undefined}
                            min={1}
                            max={336}
                            precision={0}
                            onPressEnter={(e) => {
                              e.target.blur();
                            }}
                            onBlur={(e) => {
                              setTimeout(() => {
                                updatePolicyField('overdueCollectTimeout', Number(e.target.value));
                              }, 200);
                            }}
                          />
                        </div>
                      ),
                    }}
                  />
                </div>
              </dd>
            </dl>
          </>
        )}
        {policy.enable && policy.source === 0 && type === 11 && (
          <>
            <dl>
              <dt>{t('设置生效条件')}:</dt>
              <dd>
                <div className="h-[48px] px-[12px] py-[8px] flex items-center flex-wrap">
                  <T
                    keyName="订单发货后{{X}}天仍未送达"
                    params={{
                      x: (
                        <div className="px-[4px]">
                          <InputNumber
                            key={policyFieldKey['overdueReceivedTimeout']}
                            controls={false}
                            defaultValue={policy.overdueReceivedTimeout || undefined}
                            min={5}
                            max={30}
                            precision={0}
                            onPressEnter={(e) => {
                              e.target.blur();
                            }}
                            onBlur={(e) => {
                              setTimeout(() => {
                                updatePolicyField('overdueReceivedTimeout', Number(e.target.value));
                              }, 200);
                            }}
                          />
                        </div>
                      ),
                    }}
                  />
                </div>
              </dd>
            </dl>
          </>
        )}
        {policy.enable && policy.source === 0 && type === 12 && (
          <>
            <dl>
              <dt className="self-start">{t('自动创建的会话范围')}:</dt>
              <dd className="!flex-grow">
                <div className="flex items-center">
                  <StatusCheckbox
                    checked={policy.enableKolContactStore}
                    onChange={(e) => {
                      return updatePolicy({
                        ...policy,
                        enableKolContactStore: e.target.checked,
                      })
                        .then(onUpdate)
                        .then(() => {
                          message.success(t('operation.saveSucceed'));
                        })
                        .catch(() => {
                          message.error(t('operation.saveFailed'));
                        });
                    }}
                  >
                    {t('达人联系店铺')}
                  </StatusCheckbox>
                  <StatusCheckbox
                    checked={policy.enableStoreContactKol}
                    onChange={(e) => {
                      return updatePolicy({
                        ...policy,
                        enableStoreContactKol: e.target.checked,
                      })
                        .then(onUpdate)
                        .then(() => {
                          message.success(t('operation.saveSucceed'));
                        })
                        .catch(() => {
                          message.error(t('operation.saveFailed'));
                        });
                    }}
                  >
                    {t('店铺主动联系达人')}
                  </StatusCheckbox>
                </div>
                {policy.enableStoreContactKol && (
                  <div className="bg-[#fff] px-[10px] py-[9px] mt-[10px]">
                    <div className="text-[#515359] leading-[20px] mb-[8px]">
                      {t(
                        '店铺主动联系达人：我们将在历史聊天记录中根据以下内容查找达人买家，达人买家在店铺主动联系后7天内首次回复时，会自动创建待办项。请添加当地语言内容，多项内容请分开添加。',
                      )}
                    </div>
                    <TagEditMode
                      value={policy.kolKeyword ?? []}
                      onChange={(value) => {
                        return updatePolicy({
                          ...policy,
                          kolKeyword: value,
                        })
                          .then(onUpdate)
                          .then(() => {
                            message.success(t('operation.saveSucceed'));
                          })
                          .catch(() => {
                            message.error(t('operation.saveFailed'));
                          });
                      }}
                    />
                    {get(policy, 'kolKeyword.length', 0) === 0 && (
                      <div className="text-[12px] text-[#f05b5b] leading-[18px] mt-[8px]">
                        {t('请添加内容，无内容时无法自动创建待办')}
                      </div>
                    )}
                  </div>
                )}
              </dd>
            </dl>
          </>
        )}
        <section>
          <div className="flex items-center justify-between bg-[#fff]">
            <div className="text-[#232426] font-medium">{t('solution_list')}</div>
            <div>
              <Button
                onClick={() => {
                  onEdit(policy, '', resultTypes);
                }}
                className="!px-[4px]"
                type="link"
              >
                +{t('todo_new_solution')}
              </Button>
            </div>
          </div>
          <div>
            <PolicyTable
              policy={policy}
              source={policy.source}
              dataSource={dataSource}
              onEdit={({ resultType }) => onEdit(policy, resultType, resultTypes)}
              onUpdate={onUpdate}
              pagination={false}
              className={css`
                .ant-table-row-expand-icon-spaced {
                  &:empty {
                    width: 0 !important;
                    margin-right: 0 !important;
                  }
                }
                .ant-table-row-level-1 {
                  .ant-table-cell-with-append {
                    padding-left: 40px;
                  }
                }
                .ant-table-row-expand-icon + .anticon-menu {
                  margin-left: 2px;
                  margin-top: 4px;
                }
                .ant-tag-has-color {
                  display: flex;
                }
              `}
            />
          </div>
        </section>
      </div>
    </Container>
  );
}

/**
 * tag编辑模式
 */
const TagEditMode = (props: { value: string[]; onChange: (value: string[]) => Promise<void> }) => {
  const { value, onChange: _onChange } = props;
  const { t } = useTranslate();
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const inputRef = useRef<any>(null);
  const editInputRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [inputValue]);

  const onChange = (val: string[]) => {
    setLoading(true);
    return _onChange(val).finally(() => {
      setLoading(false);
    });
  };

  const handleClose = (removedTag: string) => {
    const newValue = value.filter((tag) => tag !== removedTag);
    onChange(newValue);
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue && value.indexOf(inputValue) === -1) {
      onChange([...value, inputValue]).then(() => {
        setInputVisible(false);
        setInputValue('');
      });
    } else {
      setInputVisible(false);
      setInputValue('');
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditInputValue(e.target.value);
  };

  const handleEditInputConfirm = () => {
    const newValue = [...value];
    newValue[editInputIndex] = editInputValue;
    onChange(newValue).then(() => {
      setEditInputIndex(-1);
      setInputValue('');
    });
  };

  return (
    <>
      {value.map((tag, index) => {
        if (editInputIndex === index) {
          return (
            <div className="w-[260px] inline-block mr-[8px]">
              <Spin spinning={loading} size="small">
                <Input
                  disabled={loading}
                  ref={editInputRef}
                  key={tag}
                  size="small"
                  className="max-w-[260px]"
                  value={editInputValue}
                  placeholder={t('请输入当地语言')}
                  onChange={handleEditInputChange}
                  onBlur={handleEditInputConfirm}
                  onPressEnter={handleEditInputConfirm}
                />
              </Spin>
            </div>
          );
        }

        const isLongTag = tag.length > 20;

        const tagElem = (
          <Tag
            className="edit-tag select-none"
            key={tag}
            closable
            closeIcon={
              <CloseCircleFilled
                style={{
                  fontSize: 12,
                }}
              />
            }
            onClose={() => handleClose(tag)}
          >
            <span
              onDoubleClick={(e) => {
                setEditInputIndex(index);
                setEditInputValue(tag);
                e.preventDefault();
              }}
            >
              {isLongTag ? `${tag.slice(0, 20)}...` : tag}
            </span>
          </Tag>
        );
        return isLongTag ? (
          <Tooltip title={tag} key={tag}>
            {tagElem}
          </Tooltip>
        ) : (
          tagElem
        );
      })}
      {inputVisible && (
        <div className="w-[260px] inline-block mr-[8px]">
          <Spin spinning={loading} className="w-fit" size="small">
            <Input
              disabled={loading}
              ref={inputRef}
              type="text"
              size="small"
              className="max-w-[260px]"
              placeholder={t('请输入当地语言')}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputConfirm}
              onPressEnter={handleInputConfirm}
            />
          </Spin>
        </div>
      )}
      {!inputVisible && (
        <Tag
          className="!border-dashed select-none !border-[#3370ff] !text-[#3370ff] bg-[#f0f7ff]"
          onClick={showInput}
        >
          <PlusOutlined
            style={{
              color: '#3370ff',
            }}
          />{' '}
          {t('phrase.添加内容')}
        </Tag>
      )}
    </>
  );
};

export interface StatusCheckboxProps extends Omit<CheckboxProps, 'onChange'> {
  onChange: (...params: Parameters<Required<CheckboxProps>['onChange']>) => Promise<any>;
}
const StatusCheckbox = (props: StatusCheckboxProps) => {
  const { onChange, children, ...otherProps } = props;

  const [loading, setLoading] = useState(false);
  return (
    <Spin spinning={loading} size="small">
      <Checkbox
        onChange={useCallback(
          (e) => {
            setLoading(true);

            onChange(e).finally(() => {
              setLoading(false);
            });
          },
          [onChange],
        )}
        {...otherProps}
      >
        {children}
      </Checkbox>
    </Spin>
  );
};

const Container = styled.div`
  background: #f7f9fa;
  border: solid 1px #f0f2f5;
  > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 16px;
    border-bottom: solid 1px #f0f2f5;
    > .title {
      > h3 {
        position: relative;
        margin-bottom: 0;
        font-size: 14px;
        text-indent: 12px;
        &::before {
          content: '';
          display: inline-block;
          position: absolute;
          top: 4px;
          left: 0;
          width: 4px;
          height: 14px;
          background: var(--ant-primary-color);
        }
      }
    }
  }
  > div {
    padding: 16px;
    > dl {
      display: flex;
      align-items: center;
      > dt {
        margin-right: 6px;
        font-weight: normal;
      }
      > dd {
        margin-bottom: 0;
      }
    }
    > section {
      background: #fff;
      border: solid 1px #f0f2f5;
      padding: 8px 16px 16px;
    }
  }
`;

export default PolicyPreview;
